import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import AutoCompleteWithFreeText from 'src/components/AutoComplete/AutoCompleteWithFreeText';
import { DataSeriesField } from 'src/pages/Solutions/types/solutionAssetHierarchy';
import {
  getXAxisLabel,
  getXAxisPlaceholder,
  getXAxisTextLabel,
} from '../utils';

interface ChartConfigurationFormProps {
  chartType: string;
  chartConfiguration: {
    xAxis: { columnName: string; label: string };
    yAxis: { columnName: string; label: string };
    dataSeries: DataSeriesField[];
    showLegend: boolean;
    selectedColumns?: DataSeriesField[];
  };
  availableColumns: string[];
  onChartConfigChange: (config: any) => void;
  onValidationChange?: (
    isValid: boolean,
    errors: { [key: string]: string }
  ) => void;
  triggerValidation?: boolean;
}

export const ChartConfigurationForm: React.FC<ChartConfigurationFormProps> = ({
  chartType,
  chartConfiguration,
  availableColumns,
  onChartConfigChange,
  onValidationChange,
  triggerValidation,
}) => {
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const validateFields = (showErrors = true) => {
    const errors: { [key: string]: string } = {};

    if (chartType !== 'table') {
      if (!chartConfiguration.xAxis.columnName) {
        errors.xAxisColumn = chartType === 'metric' ? 'Value column is required' : 'X-axis column is required';
      }

      if (
        chartType !== 'radar' &&
        chartType !== 'pie' &&
        chartType !== 'metric' &&
        !chartConfiguration.yAxis.columnName
      ) {
        errors.yAxisColumn = 'Y-axis column is required';
      }

      if (
        chartType !== 'pie' &&
        chartType !== 'metric' &&
        (!chartConfiguration.dataSeries ||
          chartConfiguration.dataSeries.length === 0)
      ) {
        errors.dataSeries = 'At least one data series is required';
      } else if (chartType !== 'pie' && chartType !== 'metric') {
        const invalidSeries = chartConfiguration.dataSeries.some(
          (series, index) => !series.columnName
        );
        if (invalidSeries) {
          errors.dataSeries = 'All data series must have a column selected';
        }
      }
    }

    if (showErrors && hasUserInteracted) {
      setFieldErrors(errors);
    }

    const isValid = Object.keys(errors).length === 0;

    if (onValidationChange) {
      onValidationChange(isValid, errors);
    }

    return isValid;
  };

  useEffect(() => {
    validateFields(false);
  }, [chartConfiguration, chartType]);

  useEffect(() => {
    if (triggerValidation) {
      setHasUserInteracted(true);
      const errors: { [key: string]: string } = {};

      if (chartType !== 'table') {
        if (!chartConfiguration.xAxis.columnName) {
          errors.xAxisColumn = chartType === 'metric' ? 'Value column is required' : 'X-axis column is required';
        }

        if (
          chartType !== 'radar' &&
          chartType !== 'pie' &&
          chartType !== 'metric' &&
          !chartConfiguration.yAxis.columnName
        ) {
          errors.yAxisColumn = 'Y-axis column is required';
        }

        if (
          chartType !== 'pie' &&
          chartType !== 'metric' &&
          (!chartConfiguration.dataSeries ||
            chartConfiguration.dataSeries.length === 0)
        ) {
          errors.dataSeries = 'At least one data series is required';
        } else if (chartType !== 'pie' && chartType !== 'metric') {
          const invalidSeries = chartConfiguration.dataSeries.some(
            (series, index) => !series.columnName
          );
          if (invalidSeries) {
            errors.dataSeries = 'All data series must have a column selected';
          }
        }
      }

      setFieldErrors(errors);
      const isValid = Object.keys(errors).length === 0;
      if (onValidationChange) {
        onValidationChange(isValid, errors);
      }
    }
  }, [triggerValidation, chartType, chartConfiguration, onValidationChange]);

  const handleAxisChange = (
    axis: 'xAxis' | 'yAxis',
    field: 'columnName' | 'label',
    value: string
  ) => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    onChartConfigChange({
      ...chartConfiguration,
      [axis]: {
        ...chartConfiguration[axis],
        [field]: value,
      },
    });

    if (field === 'columnName') {
      const errorKey = axis === 'xAxis' ? 'xAxisColumn' : 'yAxisColumn';
      if (fieldErrors[errorKey]) {
        setFieldErrors((prev) => ({ ...prev, [errorKey]: '' }));
      }
    }
  };

  const handleShowLegendChange = (showLegend: boolean) => {
    onChartConfigChange({
      ...chartConfiguration,
      showLegend,
    });
  };

  const addDataSeries = () => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    onChartConfigChange({
      ...chartConfiguration,
      dataSeries: [
        ...chartConfiguration.dataSeries,
        { columnName: '', label: '' },
      ],
    });

    if (fieldErrors.dataSeries) {
      setFieldErrors((prev) => ({ ...prev, dataSeries: '' }));
    }
  };

  const removeDataSeries = (index: number) => {
    const newSeries = [...chartConfiguration.dataSeries];
    newSeries.splice(index, 1);
    onChartConfigChange({
      ...chartConfiguration,
      dataSeries: newSeries,
    });
  };

  const updateDataSeries = (
    index: number,
    field: 'columnName' | 'label',
    value: string
  ) => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    const newSeries = [...chartConfiguration.dataSeries];
    newSeries[index] = { ...newSeries[index], [field]: value };
    onChartConfigChange({
      ...chartConfiguration,
      dataSeries: newSeries,
    });

    if (fieldErrors.dataSeries) {
      setFieldErrors((prev) => ({ ...prev, dataSeries: '' }));
    }
  };

  const handleColumnSelectionChange = (
    columnName: string,
    isSelected: boolean
  ) => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    const currentSelectedColumns = chartConfiguration.selectedColumns || [];

    if (isSelected) {
      const newSelectedColumns = [
        ...currentSelectedColumns,
        { columnName, label: columnName },
      ];
      onChartConfigChange({
        ...chartConfiguration,
        selectedColumns: newSelectedColumns,
      });
    } else {
      const newSelectedColumns = currentSelectedColumns.filter(
        (col) => col.columnName !== columnName
      );
      onChartConfigChange({
        ...chartConfiguration,
        selectedColumns: newSelectedColumns,
      });
    }
  };

  const getConfigurationTitle = () => {
    if (chartType === 'table') return 'Table Configuration';
    if (chartType === 'metric') return 'Metric Configuration';
    return 'Chart Configuration';
  };

  const renderConfigurationContent = () => {
    if (chartType === 'table') {
      return (
        <Box>
          <Box>
            <AutoCompleteWithFreeText
              label="Select Columns"
              placeholder="Choose columns to display in the table"
              multiple={true}
              value={(() => {
                const tableColumns =
                  chartConfiguration.dataSeries &&
                    chartConfiguration.dataSeries.length > 0
                    ? chartConfiguration.dataSeries
                    : chartConfiguration.selectedColumns || [];

                const columnNames = tableColumns.map((col) => col.columnName);

                return columnNames;
              })()}
              options={availableColumns.map((column) => ({
                label: column,
                value: column,
              }))}
              disabled={availableColumns.length === 0}
              onChange={(e) => {
                const selectedColumnNames = e.target.value as string[];
                const newSelectedColumns = selectedColumnNames.map(
                  (columnName) => ({
                    columnName,
                    label: columnName,
                  })
                );

                if (!hasUserInteracted) {
                  setHasUserInteracted(true);
                }

                onChartConfigChange({
                  ...chartConfiguration,
                  dataSeries: newSelectedColumns,
                  selectedColumns: newSelectedColumns,
                });
              }}
            />
          </Box>
        </Box>
      );
    }

    if (chartType === 'metric') {
      return (
        <Box>
          <FormControl
            size="small"
            sx={{ mb: 1, mr: 2, minWidth: 200 }}
            error={!!fieldErrors.xAxisColumn}
          >
            <InputLabel required>Value Column</InputLabel>
            <Select
              value={chartConfiguration.xAxis.columnName || ''}
              label="Value Column"
              onChange={(e) =>
                handleAxisChange('xAxis', 'columnName', e.target.value)
              }
              disabled={availableColumns.length === 0}
              required
            >
              <MenuItem value="">
                <em>Select a column</em>
              </MenuItem>
              {availableColumns.map((column) => (
                <MenuItem key={column} value={column}>
                  {column}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.xAxisColumn && (
              <FormHelperText>{fieldErrors.xAxisColumn}</FormHelperText>
            )}
          </FormControl>

          <TextField
            label="Value Label"
            value={chartConfiguration.xAxis.label || ''}
            onChange={(e) => handleAxisChange('xAxis', 'label', e.target.value)}
            placeholder="Enter value"
            size="small"
            sx={{ mb: 1 }}
          />
        </Box>
      );
    }

    return (
      <>
        <FormControl
          size="small"
          sx={{ mb: 1, mr: 2, minWidth: 200 }}
          error={!!fieldErrors.xAxisColumn}
        >
          <InputLabel required>{getXAxisLabel(chartType)}</InputLabel>
          <Select
            value={chartConfiguration.xAxis.columnName || ''}
            label={getXAxisLabel(chartType)}
            onChange={(e) =>
              handleAxisChange('xAxis', 'columnName', e.target.value)
            }
            disabled={availableColumns.length === 0}
            required
          >
            <MenuItem value="">
              <em>Select a column</em>
            </MenuItem>
            {availableColumns.map((column) => (
              <MenuItem key={column} value={column}>
                {column}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.xAxisColumn && (
            <FormHelperText>{fieldErrors.xAxisColumn}</FormHelperText>
          )}
        </FormControl>

        <TextField
          label={getXAxisTextLabel(chartType)}
          value={chartConfiguration.xAxis.label || ''}
          onChange={(e) => handleAxisChange('xAxis', 'label', e.target.value)}
          placeholder={getXAxisPlaceholder(chartType)}
          size="small"
          sx={{ mb: 1 }}
        />

        {chartType !== 'radar' && (
          <>
            <FormControl
              size="small"
              sx={{ mb: 1, mr: 2, minWidth: 200 }}
              error={!!fieldErrors.yAxisColumn}
            >
              <InputLabel required={chartType !== 'pie'}>
                Y-Axis Column
              </InputLabel>
              <Select
                value={chartConfiguration.yAxis.columnName || ''}
                label="Y-Axis Column"
                onChange={(e) =>
                  handleAxisChange('yAxis', 'columnName', e.target.value)
                }
                disabled={availableColumns.length === 0}
                required={chartType !== 'pie'}
              >
                <MenuItem value="">
                  <em>
                    {chartType === 'pie' ? 'Optional' : 'Select a column'}
                  </em>
                </MenuItem>
                {availableColumns.map((column) => (
                  <MenuItem key={column} value={column}>
                    {column}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.yAxisColumn && (
                <FormHelperText>{fieldErrors.yAxisColumn}</FormHelperText>
              )}
            </FormControl>

            <TextField
              label={chartType === 'pie' ? 'Value Label' : 'Y-Axis Label'}
              value={chartConfiguration.yAxis.label || ''}
              onChange={(e) =>
                handleAxisChange('yAxis', 'label', e.target.value)
              }
              placeholder={
                chartType === 'pie'
                  ? 'Enter value display label'
                  : 'Enter Y-axis display label'
              }
              size="small"
              sx={{ mb: 1 }}
            />
          </>
        )}

        {chartType !== 'table' && chartType !== 'pie' && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Data Series <span style={{ color: 'red' }}>*</span></Typography>
              <Button
                onClick={addDataSeries}
                size="small"
                startIcon={<Add />}
              >
                Add Series
              </Button>
            </Box>

            {fieldErrors.dataSeries && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mb: 1 }}
              >
                {fieldErrors.dataSeries}
              </Typography>
            )}

            {chartConfiguration.dataSeries.map((series, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
              >
                <FormControl
                  size="small"
                  sx={{ minWidth: 150 }}
                  error={!series.columnName && !!fieldErrors.dataSeries}
                >
                  <InputLabel required>Column</InputLabel>
                  <Select
                    value={series.columnName}
                    label="Column"
                    onChange={(e) =>
                      updateDataSeries(index, 'columnName', e.target.value)
                    }
                    disabled={availableColumns.length === 0}
                    required
                  >
                    <MenuItem value="">
                      <em>Select column</em>
                    </MenuItem>
                    {availableColumns.map((column) => (
                      <MenuItem key={column} value={column}>
                        {column}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  placeholder="Display label"
                  value={series.label}
                  onChange={(e) =>
                    updateDataSeries(index, 'label', e.target.value)
                  }
                  size="small"
                  sx={{ minWidth: 120 }}
                />

                <Button
                  onClick={() => removeDataSeries(index)}
                  size="small"
                  color="error"
                  startIcon={<Remove />}
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={chartConfiguration.showLegend}
              onChange={(e) => handleShowLegendChange(e.target.checked)}
            />
          }
          label="Show Legend"
        />
      </>
    );
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {getConfigurationTitle()}
      </Typography>
      {renderConfigurationContent()}
    </Box>
  );
};
